import { cn } from "@lib/cn";
import { IconRobot, IconWorld } from "@tabler/icons-react";
import ReactECharts from "echarts-for-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ANALYTICS_QUERY,
  PIE_CHART_TRAFFIC_MAP,
  PIE_CHART_TRAFFIC_OPTIONS,
} from "../../constants";
import { useAnalyticsQuery } from "../../hooks";
import { EmptyStateMessage } from "../empty-state-message";

import { useIntersectionObserver } from "usehooks-ts";
import { Skeleton } from "../devices/skeleton";
import { ErrorStateMessage } from "../error-state-message";
import { CustomLegendItem } from "./custom-legend-item";

type TrafficData = {
  type: string;
  sessions: number;
};

type ChartTrafficData = TrafficData & {
  name: string;
  value: number;
  itemStyle: { color: string };
  icon: React.ElementType;
};

export const TrafficType = ({
  overrides,
  className,
}: {
  overrides?: Record<string, unknown> | undefined;
  /** Merged into the card's root, so a caller can control how it spans. */
  className?: string;
}) => {
  const chartRef = useRef<ReactECharts>(null);
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });
  const { data, isLoading, isError } = useAnalyticsQuery({
    enabled: isIntersecting,
    query: [ANALYTICS_QUERY.TRAFFIC_TYPE],
    overrides: overrides ?? {},
  });
  const trafficData = useMemo(
    () => (data?.[ANALYTICS_QUERY.TRAFFIC_TYPE] as TrafficData[]) ?? [],
    [data],
  );

  const totalSessions = useMemo(() => {
    return trafficData.reduce(
      (acc, item) => acc + (Number(item.sessions) || 0),
      0,
    );
  }, [trafficData]);

  const chartData: ChartTrafficData[] = useMemo(() => {
    if (!trafficData.length || !totalSessions) return [];
    return trafficData.map((item) => ({
      ...item,
      name: item.type,
      value: Math.round(((Number(item.sessions) || 0) / totalSessions) * 100),
      ...(PIE_CHART_TRAFFIC_MAP[
        item.type as keyof typeof PIE_CHART_TRAFFIC_MAP
      ] ?? {
        itemStyle: { color: "#CBD5E1" },
        icon: IconRobot,
      }),
    }));
  }, [trafficData, totalSessions]);

  // Every session in the range came back unlabelled. That happens both before
  // bot detection was switched on and in a genuinely quiet range, and the two
  // are indistinguishable from here - so the caveat below says what the data
  // supports ("nothing looked automated") rather than "these were all humans".
  const hasNoAutomationSignal =
    chartData.length === 1 && chartData[0].type === "Human";

  const handleLegendClick = useCallback((name: string) => {
    const chartInstance = chartRef.current?.getEchartsInstance();
    if (!chartInstance) return;
    chartInstance.dispatchAction({
      type: "legendToggleSelect",
      name,
    });
    setHiddenSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(name)) newSet.delete(name);
      else newSet.add(name);
      return newSet;
    });
  }, []);

  // Drop "hidden" entries that no longer exist in the current dataset.
  useEffect(() => {
    const availableNames = new Set(chartData.map((d) => d.type));
    setHiddenSeries((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set<string>();
      prev.forEach((name) => {
        if (availableNames.has(name)) next.add(name);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [chartData]);

  const option = useMemo(
    () => ({
      ...PIE_CHART_TRAFFIC_OPTIONS,
      series: [{ ...PIE_CHART_TRAFFIC_OPTIONS.series[0], data: chartData }],
    }),
    [chartData],
  );

  const hasData = totalSessions > 0;

  let content: React.ReactNode;
  if (isLoading)
    content = (
      <div className="tw-relative tw-size-full">
        <Skeleton />
      </div>
    );
  else if (isError) content = <ErrorStateMessage />;
  else if (!hasData)
    content = (
      <EmptyStateMessage
        title="No analytics yet, but you're on the right track"
        description="Share your Storydocs to start collecting insights. We’ll show your data as soon as it's ready."
      />
    );
  else
    content = (
      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-3">
        <div className="tw-relative -tw-ml-2 tw-flex tw-flex-1 tw-items-center tw-justify-center tw-gap-4 -sm:tw-gap-2">
          {/* Donut Chart */}
          <div className="tw-flex tw-size-[140px] tw-shrink-0 tw-items-center tw-justify-center">
            <ReactECharts
              ref={chartRef}
              option={option}
              style={{ height: "100%", width: "100%" }}
              opts={{ renderer: "svg" }}
            />
          </div>
          {/* Legend */}
          <div className="tw-flex tw-flex-col tw-justify-between tw-gap-4">
            {chartData.map((item) => (
              <CustomLegendItem
                key={item.type}
                label={item.type}
                color={item.itemStyle.color}
                Icon={item.icon}
                percentage={item.value}
                isHidden={hiddenSeries.has(item.type)}
                onToggle={handleLegendClick}
              />
            ))}
          </div>
        </div>
        {hasNoAutomationSignal && (
          <p className="tw-mx-auto tw-max-w-[500px] tw-text-center tw-font-sans tw-text-xs tw-font-normal tw-text-blue-500">
            No bot or AI signatures in this range. Detection reads what a
            visitor reports about itself, so it can’t confirm every reader was a
            person.
          </p>
        )}
      </div>
    );

  return (
    <div
      ref={ref}
      className={cn(
        "tw-flex tw-flex-col tw-gap-4 tw-rounded-xl tw-bg-white tw-p-5 tw-shadow-2xs tw-outline tw-outline-1 tw-outline-blue/5 -md:tw-min-h-[264px]",
        className,
      )}
    >
      {/* Header */}
      <div className="tw-flex tw-items-center tw-gap-2">
        <IconWorld className="tw-size-4" />
        <h2 className="tw-font-sans tw-text-base tw-font-bold tw-text-blue">
          Traffic type
        </h2>
      </div>

      {/* Content */}
      {content}
    </div>
  );
};
